import { DATA_HIDDEN_CONFIG, getReportDataHiddenConfig } from "../constants";
import {
  DataHiddenSourceFields,
  HiddenData,
  ReportHiddenData,
} from "../models/hidden-data.models";
import { Patient } from "@kells/clinic-one/apis";

type PatientWithOrgNames = Omit<Patient, "organizations"> & {
  organizations: string[];
};

export const checkIsDataHidden = (
  patient: PatientWithOrgNames,
  sourceFields: DataHiddenSourceFields
): boolean => {
  const { insurance_provider, organizations } = patient;

  const isHiddenForOrganization = sourceFields.organizations.some(
    (organization) =>
      organizations
        .map((org) => org.trim().toLowerCase())
        .includes(organization.trim().toLowerCase())
  );

  const isHiddenForInsurance = sourceFields.insurances.some(
    (insurance) =>
      insurance.trim().toLowerCase() ===
      insurance_provider?.trim().toLowerCase()
  );

  return isHiddenForOrganization || isHiddenForInsurance;
};

export const isDataHidden = (patient: PatientWithOrgNames): HiddenData => ({
  providerNetwork: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.PROVIDER_NETWORK
  ),
  talkToADentist: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.TALK_TO_A_DENTIST
  ),
  productRecommendations: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.PRODUCT_RECOMMENDATIONS
  ),
  dentalAdvisor: checkIsDataHidden(patient, DATA_HIDDEN_CONFIG.DENTAL_ADVISOR),
  assistantPanel: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.ASSISTANT_PANEL
  ),
  treatmentVerification: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.TREATMENT_VERIFICATION
  ),
  photoScanIntakeForm: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.PHOTO_SCAN_INTAKE_FORM
  ),
  photoScanIntakeFormModal: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.PHOTO_SCAN_INTAKE_FORM_MODAL
  ),
  insurance: checkIsDataHidden(patient, DATA_HIDDEN_CONFIG.INSURANCE),
  viewReports: checkIsDataHidden(patient, DATA_HIDDEN_CONFIG.VIEW_REPORTS),
  customerServiceSupport: checkIsDataHidden(
    patient,
    DATA_HIDDEN_CONFIG.CUSTOMER_SERVICE_SUPPORT
  ),
  onboarding: {
    explorePremium: checkIsDataHidden(
      patient,
      DATA_HIDDEN_CONFIG.ONBOARDING.EXPLORE_PREMIUM
    ),
  },
  upgradePlan: {
    monthly: checkIsDataHidden(
      patient,
      DATA_HIDDEN_CONFIG.UPGRADE_PLAN.MONTHLY
    ),
    annual: checkIsDataHidden(patient, DATA_HIDDEN_CONFIG.UPGRADE_PLAN.ANNUAL),
  },
});

export const isReportDataHidden = (
  patient: PatientWithOrgNames,
  isXrayReport: boolean
): ReportHiddenData => {
  const config = getReportDataHiddenConfig(isXrayReport);

  return {
    providerNetwork: checkIsDataHidden(patient, config.PROVIDER_NETWORK),
    estimatedCosts: checkIsDataHidden(patient, config.ESTIMATED_COSTS),
    isPotentialActionsHidden: checkIsDataHidden(
      patient,
      config.POTENTIAL_ACTIONS
    ),
    productRecommendations: checkIsDataHidden(
      patient,
      config.PRODUCT_RECOMMENDATIONS
    ),
    findingCategory: checkIsDataHidden(patient, config.FINDING_CATEGORY),
  };
};
